import { useEffect, useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, Input } from '@shoots/ui'
import { Header } from '../components/layout/Header'
import { Footer } from '../components/layout/Footer'
import { MARKETING_URL } from '../constants'
import { paymentSetupService } from '../services/paymentSetupService'
import { posthogService } from '../services/posthogService'
import {
  parseErrorState,
  type PaymentSetupErrorState,
} from './payment-setup-error-code'

// The branded landing for every way a payment-setup link can fail (issue #209).
//
// Before this page, four of the five failures returned text/plain from the
// `payment-setup` edge function — so an organizer who tapped a link in a text
// message ended up on the raw Supabase functions domain, reading unstyled
// monospace, with "contact support" as the only exit. There was no way to get a
// working link back without some *other* event happening to re-trigger the SMS.
//
// Now the edge function 302s here with a COARSE `state` (recoverable /
// non_recoverable) plus an opaque correlation id `eid`. The page does the one
// thing that actually unblocks a recoverable failure: re-texts the payment-setup
// link with a freshly-signed token, from the phone number they type in. The
// internal reason (missing/invalid/no-account/unavailable) is NOT in the URL —
// it is server-side telemetry only. We read `eid` and emit a client-side
// `payment_setup_error_viewed` event carrying it, so the two sides join.
//
// NOTE there is no `expired` state, despite what issue #209 assumed: the token
// is a stateless HMAC over the profile id with no expiry claim. It cannot
// expire. What expires is the Stripe Account Link it resolves to, and that
// self-heals via Stripe's refresh_url.

interface Copy {
  title: string
  body: string
}

const COPY: Record<PaymentSetupErrorState, Copy> = {
  recoverable: {
    title: "This payment setup link isn't working",
    body: 'The link may have been cut off or copied incompletely. We can text you a fresh one that works.',
  },
  non_recoverable: {
    title: 'Payment setup is temporarily unavailable',
    body: "This is on us, not you. Please try again in a few minutes. If it keeps happening, get in touch and we'll finish setting up payments with you.",
  },
}

interface RecoveryFormProps {
  /** Shown in place of the form once the request is accepted. */
  onSent: () => void
}

function RecoveryForm({ onSent }: RecoveryFormProps) {
  const [phone, setPhone] = useState('')
  const [error, setError] = useState<string | undefined>()
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setError(undefined)
    const result = await paymentSetupService.resendLink(phone)
    setSubmitting(false)
    if (result.status === 'accepted') {
      onSent()
      return
    }
    setError(
      result.status === 'invalid_phone'
        ? 'Please enter a valid phone number.'
        : "We couldn't send that just now. Please try again."
    )
  }

  return (
    <form onSubmit={handleSubmit} className="text-left" noValidate>
      <Input
        label="Your phone number"
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder="(555) 123-4567"
        value={phone}
        error={error}
        disabled={submitting}
        onChange={(e) => setPhone(e.target.value)}
      />
      <Button type="submit" loading={submitting} disabled={phone.trim() === ''}>
        Text me a fresh link
      </Button>
      <p className="mt-3 text-center text-sm text-[var(--color-text-secondary)]">
        Use the number that received the original text from Shoots.
      </p>
    </form>
  )
}

interface PaymentSetupErrorProps {
  /** Override the ?state= query param — used by the /payment-setup page, which
   * knows the outcome before it ever reaches the edge function. */
  state?: PaymentSetupErrorState
}

export function PaymentSetupError({ state: stateProp }: PaymentSetupErrorProps) {
  const [searchParams] = useSearchParams()
  const state = stateProp ?? parseErrorState(searchParams.get('state'))
  const copy = COPY[state]
  const [sent, setSent] = useState(false)

  // Correlation id: prefer the opaque `eid` the edge function minted (so this
  // client event JOINS to the server `payment_setup_error` event). When we got
  // here without hitting the function — the web-only missing-token / SPA-outage
  // path — there is no server event, so mint one so the client event still has a
  // stable id. Computed once (state initializer) so it is stable across renders.
  const [eid] = useState(() => searchParams.get('eid') || crypto.randomUUID())

  useEffect(() => {
    posthogService.capture('payment_setup_error_viewed', { eid, state })
    // Fire once per mount for this (eid, state); neither changes after mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="min-h-screen flex flex-col">
      <Header variant="light" />
      <div className="container mx-auto px-4 py-16 flex-1 flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          {sent ? (
            <>
              <div
                className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-action-primary)]/10"
                aria-hidden="true"
              >
                <svg
                  className="h-7 w-7 text-[var(--color-action-primary)]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4.5 12.75l6 6 9-13.5"
                  />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-[var(--color-text-primary)] mb-3">
                Check your texts
              </h1>
              {/* Deliberately conditional: the server answers identically whether or
                  not that number belongs to an organizer, so this page must not
                  turn into an account-existence oracle either. */}
              <p className="text-[var(--color-text-secondary)] mb-6">
                If that number belongs to a Shoots organizer, we've just texted a
                fresh payment setup link. It can take a minute to arrive.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-[var(--color-text-primary)] mb-3">
                {copy.title}
              </h1>
              <p className="text-[var(--color-text-secondary)] mb-6">
                {copy.body}
              </p>
              {state === 'recoverable' && (
                <RecoveryForm onSent={() => setSent(true)} />
              )}
            </>
          )}

          <p className="mt-8 text-sm text-[var(--color-text-secondary)]">
            Still stuck?{' '}
            <a
              href="mailto:contact@getshoots.app"
              className="font-semibold text-[var(--color-action-primary)] hover:underline"
            >
              contact@getshoots.app
            </a>
          </p>
          <div className="mt-4">
            <a
              href={MARKETING_URL}
              rel="noopener noreferrer"
              className="text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:underline"
            >
              Back to Shoots
            </a>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}
